import { FormLabel, IconButton, Input } from '@mui/material';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import { EyedropperIcon } from '@shared/components/icons/svg-icon';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import {
  ColorInputProps,
  rgbToHex,
  isValidHex
} from '@models/color-input.model.ts';
import Chrome from '@uiw/react-color-chrome';

const ColorInput: React.FC<ColorInputProps> = ({
  color,
  setColor,
  textValue,
  setTextValue,
  currentRef,
  label,
  infoTooltipKey,
  inputPlaceholder
}) => {
  const { t } = useTranslation();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAbove, setShowAbove] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTextChange = (value: string) => {
    setTextValue(value);
    if (value.startsWith('rgb')) {
      const hex = rgbToHex(value);
      if (hex) {
        setColor(hex);
      }
    } else if (isValidHex(value)) {
      const hx = value.toUpperCase();
      setColor(hx);
    }
  };

  const handleColorChange = (value: string) => {
    setColor(value);
    setTextValue(value.toUpperCase());
  };

  const openColorPicker = () => {
    if (!showColorPicker) {
      // Check available space when opening
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const pickerHeight = 300; // Approximate height of Chrome picker

        setShowAbove(spaceBelow < pickerHeight && spaceAbove > pickerHeight);
      }
    }
    setShowColorPicker(!showColorPicker);
  };

  const handleChromeColorChange = (color: any) => {
    const colorString = color.hexa || color.hex;
    setColor(colorString);
    setTextValue(colorString);
  };

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (showColorPicker && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const pickerHeight = 300;

        setShowAbove(spaceBelow < pickerHeight && spaceAbove > pickerHeight);
      }
    };

    if (showColorPicker) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [showColorPicker]);

  const openEyeDropper = async () => {
    if ('EyeDropper' in window) {
      try {
        const eyeDropper = new (window as any).EyeDropper();
        const { sRGBHex } = await eyeDropper.open();
        handleColorChange(sRGBHex);
      } catch {
        /* user canceled */
      }
    }
  };

  const stripAlpha = (hex: string) => {
    // "#RRGGBBAA" → "#RRGGBB"
    if (hex.length === 9) {
      return hex.slice(0, 7);
    }
    return hex;
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <FormLabel className="flex items-center">
        {t(label)}
        {infoTooltipKey ? <InfoTooltip text={infoTooltipKey} /> : null}
      </FormLabel>
      <div className="flex gap-4 items-center border border-(--gray-6) p-[9.5px] max-lg-xl:p-[6.5px] rounded-xl justify-between mt-[6px] max-lg-xl:mt-1">
        <div className="flex gap-2 items-center">
          <IconButton
            onClick={openColorPicker}
            sx={{
              width: 30,
              height: 28,
              border: '1px solid #ccc',
              backgroundColor: color,
              color: 'var(--white)',
              borderRadius: '10px'
            }}
          />

          <Input
            id={label}
            value={textValue}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={inputPlaceholder ?? '#RRGGBB'}
            disableUnderline
            sx={{
              minWidth: '40px',
              fontSize: '18px',
              '@media (max-width: 1715px)': {
                fontSize: '14px'
              }
            }}
          />
        </div>

        <IconButton
          onClick={openEyeDropper}
          sx={{ borderRadius: '10px', width: 30, height: 28 }}
        >
          <EyedropperIcon />
        </IconButton>
      </div>

      {showColorPicker && (
        <div
          ref={colorPickerRef}
          style={{
            position: 'absolute',
            zIndex: 9999,
            ...(showAbove
              ? { bottom: '100%', marginBottom: 2 }
              : { top: '100%', marginTop: 2 })
          }}
        >
          <style>{`
            .w-colorful {
              box-shadow: none !important;
            }
            .w-colorful * {
              position: relative !important;
            }
            .w-colorful__arrow,
            .w-colorful__arrow::before,
            .w-colorful__arrow::after,
            .w-colorful__arrow-top,
            .w-colorful__arrow-bottom,
            .w-colorful__arrow-left,
            .w-colorful__arrow-right,
            .w-colorful [class*="arrow"],
            .w-colorful [style*="border-width"],
            .w-colorful [style*="position: absolute"],
            .w-colorful [style*="top: -"],
            .w-colorful [style*="left: "],
            .w-colorful [style*="right: "],
            .w-colorful [style*="bottom: -"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              width: 0 !important;
              height: 0 !important;
              border: none !important;
              border-style: none !important;
              border-width: 0 !important;
              border-color: transparent !important;
              position: static !important;
              top: auto !important;
              left: auto !important;
              right: auto !important;
              bottom: auto !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          `}</style>
          <Chrome
            color={color}
            onChange={handleChromeColorChange}
            showAlpha={true}
            showTriangle={false}
          />
        </div>
      )}
    </div>
  );
};

export default ColorInput;
